  <template>
    <div class="p-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-6 flex items-center flex-wrap gap-y-2">
        <button
          @click="$router.push('/agents')"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Back to Agents
        </button>
        {{ isNewAgent ? "Mivu Agent" : "Edit Agent" }}
        <span v-if="isNewAgent && agentContext.length" class="ml-3 flex flex-wrap items-center gap-2">
          <span
            v-for="chip in agentContext"
            :key="chip.label"
            class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200 text-xs font-medium"
          >
            <span class="opacity-70">{{ chip.label }}:</span> {{ chip.value }}
          </span>
        </span>
      </h1>

      <div v-if="configAssociation!=null" class="w-full dark:text-gray-50 flex justify-center" :class="[isNewAgent ? 'mb-20 min-h-[72px]' : 'mb-0']">
        <div class="relative w-[80%] my-4 tracker-fade-in"
          v-if="multiformTemplate.length > 1 && (configAssociation === 'Agent' || configAssociation === 'Group' || configAssociation === 'Organisation')"
        >
          <!-- Track Background -->
          <div class="absolute top-5 left-0 right-0 h-2 transform -translate-y-1/2 bg-gray-200 dark:bg-gray-700 rounded-full"></div>

          <!-- Progress Bar Foreground -->
          <div
            :class="[binaryError ? 'bg-red-600 dark:bg-red-600' : 'bg-green-500 dark:bg-green-500']"
            class="absolute top-5 left-0 h-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-300 ease-out transform -translate-y-1/2"
            :style="{ width: (progress / (multiformTemplate.length - 1) * 100) + '%' }"
          >
          </div>

          <!-- Step Circles -->
          <div
            class="relative flex justify-between items-center w-full"
          >
            <template v-if="configAssociation === 'Agent' || configAssociation === 'Organisation' || configAssociation === 'Group'">
              <template v-for="(step, index) in multiformTemplate" :key="index">
                <div class="flex flex-col items-center relative group">
                  
                  <div
                    class="relative w-10 h-10 flex items-center justify-center rounded-full text-sm font-semibold transition-all duration-300 transform group-hover:scale-110"
                    :class="{
                      'bg-gradient-to-br from-green-500 to-emerald-600 text-gray-50 shadow-lg': progress > index || multiformComplete,
                      'bg-white dark:bg-gray-800 dark:text-gray-50 text-gray-900 border-2 border-gray-300 dark:border-gray-600': progress <= index && !multiformComplete,
                      'bg-red-600 dark:bg-red-600': binaryError !== ''
                    }"
                  >
                    <svg
                      v-if="progress > index"
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span class="font-medium" v-else>{{ index + 1 }}</span>
                  </div>
                
                  <div
                    class="mt-10 absolute text-xs font-medium text-center transition-all duration-300 whitespace-nowrap"
                    :class="{
                      'text-green-600 dark:text-green-400 font-semibold': progress > index || multiformComplete,
                      'dark:text-gray-50': progress < index
                    }"
                  >
                    {{ step.label }}
                  </div>
                </div>
              </template>
            </template>
          </div>
        </div>
      </div>

      <div v-if="loading" class="text-center py-4">
        <p class="text-gray-600">Loading...</p>
      </div>

      <div
        v-else-if="error"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong class="font-bold">Error!</strong>
        <span class="block sm:inline">{{ error }}</span>
      </div>

      <div v-else-if="configSelection && isNewAgent">
        <div
          class="flex items-center justify-center dark:text-gray-50"
          >
          <div
            class="glass-card p-6  mb-2 w-[60%] min-h-[30%] space-y-5">
            <div class="flex justify-between items-center">
              <h1 class="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">Agent Configuration</h1>
              <div class="relative group">
                <button type="button" class="hover:bg-transparent bg-transparent" @click="startTutorial">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
                </button>
                <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-1.5 py-0.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
              </div>
            </div>
            <div>
              <label for="agent-config-org" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Organisation</label>
              <GlassSelect
                id="agent-config-org"
                v-model="selectedOrg"
                @focus="clearFieldError('selectedOrg')"
                :options="orgs"
                placeholder="Select an organisation"
                :error="!!fieldErrors['selectedOrg']"
              />
              <FieldError :message="fieldErrors['selectedOrg']"/>
            </div>
            <div>
              <label for="agent-config-os" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Operating System</label>
              <GlassSelect
                id="agent-config-os"
                v-model="selectedConfig"
                @change="selectedSubset = ''; selectedArch = ''"
                @focus="clearFieldError('selectedConfig')"
                :options="configOptions"
                valueKey="os_type"
                labelKey="os_type"
                placeholder="Select an operating system"
                :error="!!fieldErrors['selectedConfig']"
              />
              <FieldError :message="fieldErrors['selectedConfig']"/>
            </div>
            <div v-if="selectedConfig && configOptions.find(o => o.os_type === selectedConfig)?.subset.length > 0">
              <label for="agent-config-os-type" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
              <GlassSelect
                id="agent-config-os-type"
                v-model="selectedSubset"
                @focus="clearFieldError('selectedSubset')"
                :options="configOptions.find(o => o.os_type === selectedConfig)?.subset || []"
                valueKey="value"
                labelKey="label"
                placeholder="Select a type"
                :error="!!fieldErrors['selectedSubset']"
              />
              <FieldError :message="fieldErrors['selectedSubset']"/>
              <p v-if="selectedSubset === 'debian'" class="mt-1 text-sm text-gray-500 dark:text-gray-400 text-center">
                Covers Debian-based distros: Ubuntu, Debian, Linux Mint, Kali Linux, Pop!_OS, and others.
              </p>
              <p v-else-if="selectedSubset === 'rhel'" class="mt-1 text-sm text-gray-500 dark:text-gray-400 text-center">
                Covers RHEL-based distros: Red Hat Enterprise Linux, CentOS, Fedora, Rocky Linux, AlmaLinux, and others.
              </p>
            </div>
            <div>
              <label for="agent-arch" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Architecture</label>
              <GlassSelect
                id="agent-arch"
                v-model="selectedArch"
                @focus="clearFieldError('selectedArch')"
                :options="archOptions"
                placeholder="Select an architecture"
                :error="!!fieldErrors['selectedArch']"
              />
              <FieldError :message="fieldErrors['selectedArch']"/>
            </div>
            <div class="text-right space-x-2">
              <button
                  @click="$router.push('/agents')"
                type="button" 
                class="btn bg-gray-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Cancel
              </button>
              <button
                @click="completeConfigSetup" 
                type="button" 
                class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>


      <div v-else-if="chooseConfigType && isNewAgent">
        <div
          class="flex items-center justify-center inset-0 dark:text-gray-50"
          >
          <div
            class=" p-6 glass-card mb-2 w-[60%] min-h-[20%] shadow-xl space-y-6">
            <div class="flex justify-between items-center">
              <h1 class="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">Agent configuration type</h1>
            </div>
            <div>
              <p v-if="isStaff" class="text-center text-base leading-relaxed text-gray-600 dark:text-gray-300 mb-4">Do you want to use an existing organisation level config, a group level config, or create a new agent specific config?</p>
              <p v-else class="text-center text-base leading-relaxed text-gray-600 dark:text-gray-300 mb-4">Create an Organisation level config</p>
              <GlassSelect
                id="agent-config-type"
                v-model="configAssociation"
                @change="validateConfigs"
                @focus="clearFieldError('configAssociation')"
                :options="isStaff ? staffConfigtypes : normalConfigTypes"
                placeholder="Select a config type"
                :error="!!fieldErrors['configAssociation']"
              />
              <FieldError :message="fieldErrors['configAssociation']"/>
            </div>
            <div v-if="configAssociation === 'Group'">
              <h1 class="text-center dark:text-white font-medium text-gray-900">Select Agent Group</h1>
              <GlassSelect
                id="agent-config-group"
                v-model="agent.group"
                @change="validateConfigs"
                @focus="clearFieldError('group')"
                :options="agent_groups"
                placeholder="Select an Agent Group"
                :error="!!fieldErrors['group']"
              />
              <FieldError :message="fieldErrors['group']"/>
            </div>
            <div class="text-right space-x-2">
              <button
                  @click="$router.push('/agents')"
                type="button" 
                class="btn bg-gray-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Cancel
              </button>
              <button
                @click="manageConfigType" 
                type="button" 
                class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <form v-else-if="showAgent" @submit.prevent="saveAgent" class="space-y-6">

        <!-- Identity header -->
        <div class="glass-card p-6">
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div class="flex items-center gap-4 min-w-0">
              <div class="w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shadow-lg">
                <svg v-if="agent?.operating_system === 'Windows'" xmlns="http://www.w3.org/2000/svg" class="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>
                <svg v-else-if="agent?.operating_system === 'Linux'" xmlns="http://www.w3.org/2000/svg" class="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 17 10 11 4 5"/><line x1="12" x2="20" y1="19" y2="19"/></svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M15 2v2"/><path d="M15 20v2"/><path d="M2 15h2"/><path d="M2 9h2"/><path d="M20 15h2"/><path d="M20 9h2"/><path d="M9 2v2"/><path d="M9 20v2"/></svg>
              </div>
              <div class="min-w-0">
                <div class="flex items-center gap-2 min-w-0">
                  <h2 class="text-xl font-bold text-gray-900 dark:text-gray-50 truncate">
                    {{ agent?.licensekey || "New Mivu Agent" }}
                  </h2>
                  <button
                    v-if="agent?.licensekey"
                    type="button"
                    @click="copyLicenseKey"
                    :title="licenseKeyCopied ? 'Copied!' : 'Copy license key'"
                    class="shrink-0 p-1.5 rounded-md bg-transparent hover:bg-gray-200/60 dark:hover:bg-gray-700/60 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
                  >
                    <svg v-if="licenseKeyCopied" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                  </button>
                </div>
                <div class="mt-2 flex flex-wrap items-center gap-2">
                  <span :class="['inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold', currentLicenseStateMeta.pillClass]">
                    <span :class="['w-1.5 h-1.5 rounded-full', currentLicenseStateMeta.dotClass]"></span>
                    {{ agent?.license_state || "No license state" }}
                  </span>
                  <span v-if="agentOrgName" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-200/70 dark:bg-gray-700/60 text-gray-700 dark:text-gray-200 text-xs font-medium">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>
                    {{ agentOrgName }}
                  </span>
                  <span v-if="agentGroupName" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs font-medium">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/></svg>
                    {{ agentGroupName }}
                  </span>
                  <span v-if="agent?.last_update_status === 'failed'" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/20 text-red-500 text-xs font-semibold">
                    <span class="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                    Update failed
                  </span>
                  <span v-if="isNewAgent && selectedTypeLabel" class="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-200/70 dark:bg-gray-700/60 text-gray-700 dark:text-gray-200 text-xs font-medium">
                    {{ selectedTypeLabel }}
                  </span>
                  <span v-if="isNewAgent && selectedArch" class="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-200/70 dark:bg-gray-700/60 text-gray-700 dark:text-gray-200 text-xs font-medium">
                    {{ selectedArch }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Quick actions -->
            <div class="flex flex-wrap items-center gap-2">
              <a
                v-if="agent?.hostname"
                :href="grafanaDahsboardUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-2 text-white bg-gradient-to-br from-purple-600 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-4 py-2 no-underline hover:no-underline"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 14 4-4"/><path d="M3.34 19a10 10 0 1 1 17.32 0"/></svg>
                Dashboard
                <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>
              </a>
              <button
                v-if="product?.link"
                type="button"
                @click="downloadFile"
                class="inline-flex items-center gap-2 text-white bg-gradient-to-br from-purple-600 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-4 py-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17V3"/><path d="m6 11 6 6 6-6"/><path d="M19 21H5"/></svg>
                Download Agent
              </button>
              <a ref="downloadLink" style="display:none;"></a>
            </div>
          </div>
        </div>

        <!-- Last update outcome -->
        <div v-if="agent?.last_update_status === 'failed'" class="glass-card p-6 border border-red-400/60">
          <div class="flex items-center justify-between gap-2 mb-3">
            <div class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
              <h3 class="text-xs font-semibold uppercase tracking-widest text-red-500">Last Update Failed</h3>
            </div>
            <span v-if="agent?.last_update_at" class="text-xs text-gray-500 dark:text-gray-400">{{ formatDateTime(agent.last_update_at) }}</span>
          </div>
          <p class="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">
            {{ agent?.last_update_reason || "The agent did not apply the last update and is running its previous configuration." }}
          </p>
        </div>

        <!-- License state -->
        <div class="glass-card p-6">
          <div class="flex items-center gap-2 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-blue-600 dark:text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>
            <h3 class="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">License State</h3>
          </div>
          <div id="license_state" class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <button
              v-for="option in licenseStateOptions"
              :key="option.value"
              :id="`license-state-${option.value}`"
              type="button"
              @click="setLicenseState(option.value)"
              :class="[
                'flex items-center gap-3 rounded-xl border p-3 text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50',
                agent?.license_state === option.value
                  ? option.activeClass
                  : 'border-gray-200 dark:border-gray-600/60 bg-white/50 dark:bg-gray-800/40 text-gray-700 dark:text-gray-200 hover:border-blue-400/60 hover:bg-blue-500/5'
              ]"
            >
              <span :class="['w-9 h-9 shrink-0 rounded-lg flex items-center justify-center', agent?.license_state === option.value ? option.iconClass : 'bg-gray-200/70 dark:bg-gray-700/60 text-gray-500 dark:text-gray-400']">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle v-if="option.iconCircle" cx="12" cy="12" r="10"/>
                  <path v-for="(d, i) in option.iconPaths" :key="i" :d="d"/>
                </svg>
              </span>
              <span class="min-w-0">
                <span class="block text-sm font-semibold">{{ option.value }}</span>
                <span class="block text-xs opacity-70">{{ option.hint }}</span>
              </span>
            </button>
          </div>
          <FieldError :message="fieldErrors['license_state']"/>
        </div>

        <!-- Assignment -->
        <div class="glass-card p-6">
          <div class="flex items-center gap-2 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-blue-600 dark:text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="16" y="16" width="6" height="6" rx="1"/><rect x="2" y="16" width="6" height="6" rx="1"/><rect x="9" y="2" width="6" height="6" rx="1"/><path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3"/><path d="M12 12V8"/></svg>
            <h3 class="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">Assignment</h3>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div id="organization" class="flex items-center gap-3 rounded-xl border border-gray-200/80 dark:border-gray-600/50 bg-gray-100/60 dark:bg-gray-800/40 px-4 py-3">
              <span class="w-9 h-9 shrink-0 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Organisation</span>
                <span class="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ agentOrgName || "—" }}</span>
              </span>
              <span class="shrink-0 text-gray-400 dark:text-gray-500" title="Locked to the initial selection">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </span>
            </div>

            <div id="operating_system" class="flex items-center gap-3 rounded-xl border border-gray-200/80 dark:border-gray-600/50 bg-gray-100/60 dark:bg-gray-800/40 px-4 py-3">
              <span class="w-9 h-9 shrink-0 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Operating System</span>
                <span class="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ agent?.operating_system || "—" }}</span>
              </span>
              <span class="shrink-0 text-gray-400 dark:text-gray-500" title="Locked to the initial selection">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </span>
            </div>

            <div v-if="isNewAgent && selectedSubset" class="flex items-center gap-3 rounded-xl border border-gray-200/80 dark:border-gray-600/50 bg-gray-100/60 dark:bg-gray-800/40 px-4 py-3">
              <span class="w-9 h-9 shrink-0 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/><path d="M12 22V12"/><polyline points="3.29 7 12 12 20.71 7"/></svg>
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Type</span>
                <span class="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ selectedTypeLabel || selectedSubset }}</span>
              </span>
              <span class="shrink-0 text-gray-400 dark:text-gray-500" title="Locked to the initial selection">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </span>
            </div>

            <div v-if="isNewAgent && selectedArch" class="flex items-center gap-3 rounded-xl border border-gray-200/80 dark:border-gray-600/50 bg-gray-100/60 dark:bg-gray-800/40 px-4 py-3">
              <span class="w-9 h-9 shrink-0 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M15 2v2"/><path d="M15 20v2"/><path d="M2 15h2"/><path d="M2 9h2"/><path d="M20 15h2"/><path d="M20 9h2"/><path d="M9 2v2"/><path d="M9 20v2"/></svg>
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Architecture</span>
                <span class="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ selectedArch }}</span>
              </span>
              <span class="shrink-0 text-gray-400 dark:text-gray-500" title="Locked to the initial selection">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </span>
            </div>

            <div v-if="showGroupField" class="md:col-span-2">
              <label for="group" class="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-white">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-indigo-500 dark:text-indigo-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/></svg>
                Agent Group
              </label>
              <GlassSelect
                id="group"
                v-model="agent.group"
                :options="agent_groups"
                searchable
                placeholder="Select an Agent Group"
                :error="!!fieldErrors['group']"
                @focus="clearFieldError('group')"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Switching group moves this agent under that group's configuration.</p>
              <FieldError :message="fieldErrors['group']"/>
              <FieldError :message="fieldErrors['non_field_errors']"/>
            </div>
          </div>
          <FieldError :message="fieldErrors['organization']"/>
          <FieldError :message="fieldErrors['operating_system']"/>
        </div>

        <!-- Connection details -->
        <div v-if="agent?.hostname || agent?.username" class="glass-card p-6">
          <div class="flex items-center gap-2 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-blue-600 dark:text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 17 10 11 4 5"/><line x1="12" x2="20" y1="19" y2="19"/></svg>
            <h3 class="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">Connection Details</h3>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div v-if="agent.hostname">
              <label for="hostname" class="block text-sm font-medium text-gray-700 dark:text-white">Hostname</label>
              <div class="relative mt-1">
                <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 dark:text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="8" x="2" y="2" rx="2" ry="2"/><rect width="20" height="8" x="2" y="14" rx="2" ry="2"/><line x1="6" x2="6.01" y1="6" y2="6"/><line x1="6" x2="6.01" y1="18" y2="18"/></svg>
                </span>
                <input
                  type="text"
                  id="hostname"
                  v-model="agent.hostname"
                  required
                  :class="[ 'w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2 focus:outline-none focus:ring-2', fieldErrors['hostname']
                        ? 'ring-2 ring-red-500  focus:ring-red-500'
                        : 'focus:ring-blue-500'
                  ]"
                />
              </div>
              <FieldError :message="fieldErrors['hostname']"/>
            </div>

            <div v-if="agent.username">
              <label for="username" class="block text-sm font-medium text-gray-700 dark:text-white">Username</label>
              <div class="relative mt-1">
                <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 dark:text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </span>
                <input
                  type="text"
                  id="username"
                  v-model="agent.username"
                  required
                  :class="[ 'w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2 focus:outline-none focus:ring-2', fieldErrors['username']
                        ? 'ring-2 ring-red-500  focus:ring-red-500'
                        : 'focus:ring-blue-500'
                  ]"
                />
              </div>
              <FieldError :message="fieldErrors['username']"/>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex flex-wrap items-center justify-end gap-3">
          <span v-if="isDirty" class="mr-auto inline-flex items-center gap-2 text-xs font-medium text-amber-600 dark:text-amber-400">
            <span class="relative flex h-2 w-2">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
            </span>
            Unsaved changes
          </span>
          <button
            type="button"
            @click="$router.push({ name: 'AgentView' })"
            class="btn bg-gray-600 text-white text-sm font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button v-if="isStaff || canChangeAgent || canAddAgent"
            id="submit-agent-btn"
            type="submit"
            :disabled="loading"
            class="btn inline-flex items-center gap-2 shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <svg v-if="loading" class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
            <svg v-else-if="isNewAgent" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/><path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7"/><path d="M7 3v4a1 1 0 0 0 1 1h7"/></svg>
            {{ isNewAgent ? "Create Agent" : "Save Changes" }}
          </button>
        </div>
      </form>

    <ConfigWorkSpaceDetails
      v-else-if="showCollector"
      :configType="'collectorconfig'"
      :isMultiSubmit="true"
      :multiSubmitAgent="multistepAgent"
      :multiSubmitGroup="agent?.group"
      :multiSubmitOrg="selectedOrg"
      :multiSubmitOS="selectedConfig"
      :multiSubmitSpaceType="spaceType"
      @collector-submitted="handleNextStep"
    />

    <ConfigWorkSpaceDetails
      v-else-if="showAlloy"
      :configType="'alloyconfig'"
      :isMultiSubmit="true"
      :multiSubmitAgent="multistepAgent"
      :multiSubmitGroup="agent?.group"
      :multiSubmitOrg="selectedOrg"
      :multiSubmitOS="selectedConfig"
      :multiSubmitSpaceType="spaceType"
      @collector-submitted="handleNextStep"
    />

    <template v-if="showCollectorFiles">
      <div class="flex items-center justify-center inset-0 dark:text-gray-50">
        <div class="glass-card p-6  mb-2 w-[60%] min-h-[20%] shadow-xl space-y-6">
          
          <div class="space-y-2">
            <label for="file" class="block text-sm font-medium text-gray-700 dark:text-white">
              File (Max 4 files) - Optional
            </label>
            
            <input
              type="file"
              id="file"
              ref="fileInput"
              @change="handleFileUpload"
              multiple
              class="hidden"
            />
            
            <div class="flex space-x-2 dark:bg-gray-700 bg-gray-200 rounded truncate">
              <button
                type="button"
                @click="triggerFileInput"
                :disabled="files.length >= 4"
                class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Choose File
              </button>
              
              <div v-if="files.length > 0" class="flex items-center text-sm text-gray-600 dark:text-gray-300">
                {{ files.length }} file(s) selected
              </div>
            </div>
            
            <!-- File List -->
            <div v-if="files.length > 0" class="mt-4 space-y-2">
              <div
                v-for="(file, index) in files"
                :key="index"
                class="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-700 rounded-md"
              >
                <span class="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                  {{ file.name }}
                </span>
                
                <button
                  type="button"
                  @click="removeFile(index)"
                  class="ml-2 text-gray-900 dark:text-gray-50 focus:outline-none"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <p class="text-red-500">{{ binaryError }}</p>
            <div class="mt-4 text-right space-x-2">
              <button
                @click="handleNextStep()" 
                type="button" 
                class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Next
              </button>
            </div>
          </div>

        </div>
      </div>
    </template>

    <!-- Proxy (Optional): baked into the installer's launcher config so
         firewalled agents can make first contact -->
    <template v-if="showProxy">
      <div class="flex items-center justify-center inset-0 dark:text-gray-50">
        <div class="glass-card p-6 mb-2 w-[60%] min-h-[20%] shadow-xl space-y-6">
          <div class="space-y-2">
            <div class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-blue-600 dark:text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>
              <h3 class="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Proxy <span class="normal-case font-normal tracking-normal text-gray-400 dark:text-gray-500">(Optional, leave empty for a direct connection)</span>
              </h3>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-300">
              If this agent sits behind a firewall that only allows outbound traffic through a web proxy,
              enter the proxy here (http:// or https://). Agent traffic stays TLS-encrypted end to end;
              the proxy only tunnels it. The value is written into the installer's launcher configuration
              so the agent can reach the licensing server on first contact.
            </p>

            <label for="agent_proxy_url" class="block text-sm font-medium text-gray-700 dark:text-white">Proxy URL</label>
            <div class="relative mt-1">
              <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 dark:text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>
              </span>
              <input
                id="agent_proxy_url"
                v-model="proxyUrl"
                type="url"
                placeholder="http://proxy.example.com:3128"
                @focus="clearFieldError('proxy_url')"
                :class="[ 'w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2 focus:outline-none focus:ring-2 dark:bg-gray-600 dark:text-gray-50', fieldErrors['proxy_url']
                      ? 'ring-2 ring-red-500 focus:ring-red-500'
                      : 'focus:ring-blue-500'
                ]"
              />
            </div>
            <FieldError :message="fieldErrors['proxy_url']"/>

            <div class="mt-4 text-right space-x-2">
              <button
                @click="skipProxy"
                type="button"
                class="btn bg-gray-600 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Skip
              </button>
              <button
                @click="applyProxy"
                type="button"
                class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>


    <div v-if="showFileUpload"></div>

    <div class="flex justify-center mt-10" v-if="generatingBinary || multiformComplete || binaryGenerationFailed">  
      <div
        v-if="generatingBinary"
        class="glass-card text-center p-8 font-semibold w-[80%]"
      >
        <svg
          class="animate-spin h-10 w-10 mr-2 inline-block text-gray-800 dark:text-gray-50"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z"
          ></path>
        </svg>
        <p class="text-gray-800 dark:text-gray-50">Uploading installer components, This may take a moment</p>
        <p class="text-gray-600 dark:text-gray-300 text-sm mt-1">Please keep this tab open. Leaving or refreshing now will cancel the build.</p>
      </div>


      <div
        v-if="binaryGenerationFailed"
        class="flex flex-col glass-card items-center text-center p-8 dark:text-gray-50 font-semibold w-[80%]"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="lucide lucide-circle-x-icon lucide-circle-x w-20 h-20 text-red-600"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="m15 9-6 6" />
          <path d="m9 9 6 6" />
        </svg>
        <p class="text-gray-800 dark:text-gray-50">MivuInstaller Generation - Failed</p>
        <p class="text-gray-800 dark:text-gray-50">{{ binaryError }}</p>
        <div 
          class="flex items-center justify-center"
          >
          <div class="mt-6 flex space-x-3">
            <button
              @click="$router.push('/agents')"
              class="text-white bg-blue-600 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2"
            >
              Back to Agents
            </button>
          </div>
        </div>
      </div>

      <div 
        v-if="multiformComplete"
        class="glass-card text-center p-8 font-semibold w-[80%]"
      >
        <svg xmlns="http://www.w3.org/2000/svg"
          width="30" height="30"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="h-10 w-10 mr-2 inline-block text-green-600 lucide lucide-thumbs-up-icon lucide-thumbs-up">
          <path d="M7 10v12"/>
          <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/>
        </svg>
        <p class="text-gray-800 dark:text-gray-50">{{ buildStarted ? 'MivuInstaller build started' : 'MivuInstaller Ready' }}</p>
        <p v-if="buildStarted" class="text-gray-600 dark:text-gray-300 text-sm mt-1">
          This can take a few minutes. Track its progress in the <span class="font-semibold">Downloads</span> tab, it keeps running even if you leave this page or reload.
        </p>
        <div v-for="message in binaryCreationMessages">
          <div>{{ message }}</div>
        </div>
        <div
          class="flex items-center justify-center"
          >
          <div v-if="buildStarted" class="mt-6 flex space-x-3">
            <button
              @click="$router.push({ name: 'AgentView', query: { tab: 'downloads' } })"
              class="text-white bg-blue-500 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2 text-center me-2 mb-2"
            >
              Go to Downloads
            </button>
          </div>
          <div v-else-if="product?.link" class="mt-6 flex space-x-3">
            <button
              @click="$router.push('/agents')"
              class="text-white bg-blue-600 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2"
            >
              Back to Agents
            </button>
            <button
              class="text-white bg-gradient-to-br from-purple-600 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2"
              @click="downloadFile"
            >
            <div class="flex space-x-2">
              <p class="font-medium">Download Agent</p>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-down-to-line-icon lucide-arrow-down-to-line"><path d="M12 17V3"/><path d="m6 11 6 6 6-6"/><path d="M19 21H5"/></svg>
            </div>
            </button>
            <a ref="downloadLink" style="display:none;"></a>
          </div>
        </div>
      </div>
    </div>
    </div>
  </template>

  <script>

  import { createAgent, getAgents, updateAgent } from "@/services/agentService";
  import { getProducts } from "@/services/productsService";
  import { getNoAgentProducts } from "@/services/productsNoAgentService";
  import { buildJobsStore } from "@/store/buildJobs";
  import { getOrgs } from "@/services/wsOrgService";
  import { getAgentGroups } from "@/services/agentGroupService";
  import { useStore } from "@/store";
  import FieldError from "@/components/FieldError.vue";
  import ConfigWorkSpaceDetails from "./ConfigWorkSpaceDetails.vue";
  import { getApplicableConfigs } from "@/services/configService";
  import { getBuildManifest } from "@/services/workspaceManifest";
  import { getMyAccount } from "@/services/accountService";
  import { getAllUsers } from "@/services/userService";
  import { getWorkspaceUsers, getWorkspaceUsersbyOrg } from "@/services/workspaceUsersService";
  import GlassSelect from "@/components/GlassSelect.vue";
  import { useTutorial } from "@/composables/tutorial";

  // Subsets whose installers ship Alloy and therefore need an alloy config step.
  const ALLOY_SUBSETS = ['serverspace', 'workspace', 'debian', 'rhel'];

  export default {
    name: "OrganizationDetails",
    setup() {
      const { start } = useTutorial();
      return { _start: start };
    },
    components: {
      FieldError,
      ConfigWorkSpaceDetails,
      GlassSelect,
    },
    data() {
      return {
        // UI State & Navigation
        showAgent: false,
        showCollector: false,
        showAlloy: false,
        showFileUpload: false,
        chooseConfigType: false,
        configSelection: true,
        showCollectorFiles: false,
        showProxy: false,
        proxyUrl: "",
        
        // Permissions & Access Control
        allowConfigs: false,
        canChangeAgent: false,
        canAddAgent: false,
        
        // Multiform/Binary Generation
        multiformTemplate: [],
        multiformComplete: false,
        buildStarted: false,
        generatingBinary: false,
        binaryGenerationFailed: false,
        binaryError: "",
        progress: 0,
        
        // Configuration
        configAssociation: "",
        selectedConfig: "",
        selectedOrg: "",
        selectedSubset: "",
        selectedArch: "",
        archOptions: ["x86_64", "arm64"],
        configOptions: [
          { os_type: "Windows", subset: [
            { value: "workspace",   label: "Workspace" },
            { value: "serverspace", label: "Serverspace" },
          ]},
          { os_type: "Linux", subset: [
            { value: "debian",  label: "Debian-based" },
            { value: "rhel", label: "RHEL-based" },
          ]},
          // { os_type: "Mac", subset: [] },
        ],
        staffConfigtypes: ["Organisation", "Group", "Agent"],
        normalConfigTypes: ["Organisation"],
        
        // Agent Data
        agent: {
          hostname: "",
          organization: null,
          license_state: "Unapproved",
          username: "",
          licensekey: "",
          operating_system: "",
          architecture: "",
          distro: "",
          group: null,
          id: null
        },
        multistepAgent: null,
        isNewAgent: false,
        
        // Product Data
        product: {
          name: "MivuInstaller",
          link: "",
          organization: "",
          agent: "",
        },
        noagentproducts: [],
        
        // Organizations & Groups
        orgs: [],
        agent_groups: [],
        orgInstallers: [],
        groupInstallers: [],
        
        // Users/Workspace
        workspaceUser: "",
        workspaceUsers: [],
        
        // Edit-form UI state
        licenseKeyCopied: false,
        originalAgentSnapshot: null,

        // Loading & Error States
        loading: false,
        error: null,
        fieldErrors: {},
        
        // External Links
        grafanaDashboard: process.env.VUE_APP_GRAFANA_DASHBOARD_URL,


        files: [],

        binaryCreationMessages: []
      };
    },
    computed: {
      spaceType(){
        return this.selectedSubset === 'workspace' ? 'workspace' : 'serverspace';
      },
      isStaff(){
        return useStore.isStaff();
      },
      user() {
        return useStore.getUser();
      }, 
      formattedAgent() {
        return {
          ...this.agent
        };
      },
      grafanaDahsboardUrl(){
        return `${this.grafanaDashboard}?var-host=${this.agent.hostname}`
      },
      currentSubsetOptions() {
        const match = this.configOptions.find(item => item.os_type === this.selectedConfig);
        return match ? match.subset : [];
      },
      selectedOrgName() {
        return this.orgs.find(o => o.id == this.selectedOrg)?.name || null;
      },
      selectedTypeLabel() {
        return this.currentSubsetOptions.find(s => s.value === this.selectedSubset)?.label || null;
      },
      agentContext() {
        const contextList = [];
        if (this.selectedOrgName) contextList.push({ label: "Org", value: this.selectedOrgName });
        if (this.selectedConfig) contextList.push({ label: "OS", value: this.selectedConfig });
        if (this.selectedTypeLabel) contextList.push({ label: "Type", value: this.selectedTypeLabel });
        if (this.selectedArch) contextList.push({ label: "Arch", value: this.selectedArch });
        return contextList;
      },
      agentOrgName() {
        return this.orgs.find(o => o.id == this.agent?.organization)?.name || null;
      },
      agentGroupName() {
        return this.agent_groups.find(g => g.id == this.agent?.group)?.name || null;
      },
      showGroupField() {
        return this.configAssociation === 'Group'
          || this.configAssociation === 'Agent'
          || !!this.agent?.group
          || !this.isNewAgent;
      },
      isDirty() {
        return !this.isNewAgent
          && !!this.originalAgentSnapshot
          && JSON.stringify(this.agent) !== this.originalAgentSnapshot;
      },
      licenseStateOptions() {
        return [
          {
            value: 'Active',
            hint: 'Licensed and reporting',
            iconCircle: true,
            iconPaths: ['m9 12 2 2 4-4'],
            activeClass: 'border-green-500/70 bg-green-500/10 hover:bg-green-500/15 text-green-700 dark:text-green-300 ring-1 ring-green-500/40',
            iconClass: 'bg-green-500/15 text-green-600 dark:text-green-400',
            pillClass: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
            dotClass: 'bg-green-500',
          },
          {
            value: 'Unapproved',
            hint: 'Awaiting approval',
            iconPaths: [
              'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
              'M9.1 9a3 3 0 0 1 5.82 1c0 2-3 3-3 3',
              'M12 17h.01',
            ],
            activeClass: 'border-blue-500/70 bg-blue-500/10 hover:bg-blue-500/15 text-blue-700 dark:text-blue-300 ring-1 ring-blue-500/40',
            iconClass: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
            pillClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
            dotClass: 'bg-blue-500',
          },
          {
            value: 'Pending',
            hint: 'Activation in progress',
            iconCircle: true,
            iconPaths: ['M12 6v6l4 2'],
            activeClass: 'border-amber-500/70 bg-amber-500/10 hover:bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-1 ring-amber-500/40',
            iconClass: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
            pillClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
            dotClass: 'bg-amber-500',
          },
          {
            value: 'HardStop',
            hint: 'Blocked from running',
            iconPaths: [
              'm15 9-6 6',
              'M2.586 16.726A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2h6.624a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586z',
              'm9 9 6 6',
            ],
            activeClass: 'border-red-500/70 bg-red-500/10 hover:bg-red-500/15 text-red-700 dark:text-red-300 ring-1 ring-red-500/40',
            iconClass: 'bg-red-500/15 text-red-600 dark:text-red-400',
            pillClass: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
            dotClass: 'bg-red-500',
          },
        ];
      },
      currentLicenseStateMeta() {
        return this.licenseStateOptions.find(o => o.value === this.agent?.license_state)
          || { pillClass: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200', dotClass: 'bg-gray-400' };
      },
      agentUrlState() {
        return {
          org: this.selectedOrg,
          os: this.selectedConfig,
          type: this.selectedSubset,
          arch: this.selectedArch,
          configType: this.configAssociation,
        };
      }
    },
    async created() {
      this.checkPermissions();

      // agent id
      const agentId = this.$route.params.id;

      if (agentId === "new") {
        this.isNewAgent = true;
        await Promise.all([
          this.fetchWorkspaceUser(),
          this.fetchOrg(),
          this.fetchGroups(),
        ]);
        this.fetchNoAgentProducts();
        this.hydrateFromUrl();
      } else {
        this.showAgent = true;
        await Promise.all([
          this.fetchWorkspaceUser(),
          this.fetchAgent(agentId),
          this.fetchOrg(),
          this.fetchProducts(agentId),
          this.fetchGroups(),
        ]);
      }
    },
    mounted() {
      window.addEventListener('beforeunload', this.warnIfBuilding);
    },
    beforeUnmount() {
      window.removeEventListener('beforeunload', this.warnIfBuilding);
    },
    beforeRouteLeave(to, from, next) {
      if (this.generatingBinary) {
        const leave = window.confirm(
          'The installer is still uploading. Leaving now will cancel the build. Leave anyway?'
        );
        if (!leave) return next(false);
      }
      next();
    },
    watch : {
      user: {
        handler(user) {
          if (user) {
            this.checkPermissions();
          }
        },
        immediate: true
      },
      selectedOrg: {
        handler(newOrg) {
          this.filterGroupsByOrg();
        },
        immediate: true
      },
      agentUrlState: {
        handler() {
          if (this.isNewAgent) this.syncUrlState();
        },
        deep: true
      }
    },
    methods: {

      syncUrlState(){
        if (this._hydratingUrl) return;
        const query = {};
        if (this.selectedOrg) query.org = String(this.selectedOrg);
        if (this.selectedConfig) query.os = this.selectedConfig;
        if (this.selectedSubset) query.type = this.selectedSubset;
        if (this.selectedArch) query.arch = this.selectedArch;
        if (this.configAssociation) query.configType = this.configAssociation;
        this.$router.replace({ query }).catch(() => {});
      },

      hydrateFromUrl(){
        const q = this.$route.query;
        this._hydratingUrl = true;
        if (q.org != null) {
          const match = this.orgs.find(o => String(o.id) === String(q.org));
          if (match) this.selectedOrg = match.id;
        }
        if (q.os && this.configOptions.some(o => o.os_type === q.os)) {
          this.selectedConfig = q.os;
        }
        if (q.type && this.currentSubsetOptions.some(s => s.value === q.type)) {
          this.selectedSubset = q.type;
        }
        if (q.arch && this.archOptions.includes(q.arch)) {
          this.selectedArch = q.arch;
        }
        if (q.configType) {
          const allowed = this.isStaff ? this.staffConfigtypes : this.normalConfigTypes;
          if (allowed.includes(q.configType)) this.configAssociation = q.configType;
        }
        this.$nextTick(() => { this._hydratingUrl = false; });
      },

      // CONFIG OS AND ORG
      async completeConfigSetup(){
        this.validateAgentData();
        if (Object.keys(this.fieldErrors).length > 0) return;

        const hasSubset = this.configOptions.find(item => item.os_type === this.selectedConfig)?.subset.length > 0;
        if (this.selectedConfig && this.selectedOrg && (!hasSubset || this.selectedSubset)){
          this.configSelection = false;
          this.agent.organization = this.selectedOrg;
          this.agent.operating_system = this.selectedConfig;
          this.agent.architecture = this.selectedArch;
          this.agent.distro = this.selectedSubset;
          this.chooseConfigType = true;

          if (this.configAssociation) await this.validateConfigs();
        }
        await this.fetchWorkspaceUsers();
      },
 
      // CONFIG TYPE
      async manageConfigType(){

        this.validateAgentAssocisationData();
        if (Object.keys(this.fieldErrors).length > 0) return;

        const isOrganisation = this.configAssociation === "Organisation";
        const isGroup = this.configAssociation === "Group" && this.agent.group !== null;
        const isAgent = this.configAssociation === "Agent";

        if (isOrganisation || isGroup || isAgent) {
          if (!this.multiformTemplate.length) await this.validateConfigs();
          this.handleNextStep();
        }
      },

      handleNextStep(){
        const currentStep = this.multiformTemplate[this.progress].type
        this[currentStep] = false;
        this.incrementProgress();
        const nextStep = this.multiformTemplate[this.progress].type;
        this[nextStep] = true;

        if (nextStep === 'generatingBinary'){
          this.submitForm()
        }
      },

      setLicenseState(value){
        this.agent.license_state = value;
        this.clearFieldError('license_state');
      },

      async copyLicenseKey(){
        if (!this.agent?.licensekey) return;
        try {
          await navigator.clipboard.writeText(this.agent.licensekey);
          this.licenseKeyCopied = true;
          setTimeout(() => { this.licenseKeyCopied = false; }, 2000);
        } catch (error) {
          console.error("Failed to copy license key:", error);
        }
      },

      clearFieldError(fieldName){
        if (this.fieldErrors[fieldName]) {
          delete this.fieldErrors[fieldName];
          delete this.fieldErrors['non_field_errors'];
        }
      },

      validateAgentData(){
        this.fieldErrors = {};
        if (this.selectedOrg === ""){
          this.fieldErrors['selectedOrg'] = "Please select an organisation";
        }
        if (this.selectedArch === ""){
          this.fieldErrors['selectedArch'] = "Please select an architecture";
        }
        if (this.selectedConfig === ""){
          this.fieldErrors['selectedConfig'] = "Please select an operating system";
        }
        const hasSubset = this.configOptions.find(item => item.os_type === this.selectedConfig)?.subset.length > 0;
        if (hasSubset && this.selectedSubset === ""){
          this.fieldErrors['selectedSubset'] = "Please select a type";
        }
      },

      validateAgentAssocisationData(){
        this.fieldErrors = {};
        if (this.configAssociation === ""){
          this.fieldErrors['configAssociation'] = "Please select a config association"
        }
        if (this.configAssociation === "Group" && this.agent.group === null ){
          this.fieldErrors['group'] = "Please select a group"
        }
      },

      // VALIDATE CONFIGS
      async validateConfigs(){
        try {

          await this.$nextTick();

          const needsAlloyConfig = ALLOY_SUBSETS.includes(this.selectedSubset);

          const filterTemplate = (template) => {
            return this.isStaff
              ? template
              : template.filter(item => item.type !== 'showCollectorFiles');
          };

          const buildTemplate = (existingConfigs = [], { withAgentStep = false } = {}) => {
            const existingTypes = new Set(existingConfigs.map(c => c.config_type));
            const needsCollector = !existingTypes.has('collectorconfig');
            const needsAlloy = needsAlloyConfig && !existingTypes.has('alloyconfig');

            const steps = [{ label: 'Agent Config Type', type: 'chooseConfigType' }];
            if (withAgentStep) steps.push({ label: 'Create Agent', type: 'showAgent' });
            if (needsCollector) steps.push({ label: 'Add Collector Config', type: 'showCollector' });
            if (needsAlloy) steps.push({ label: 'Add Alloy Config', type: 'showAlloy' });
            steps.push({ label: 'Proxy (Optional)', type: 'showProxy' });
            steps.push({ label: 'Add Collector Files', type: 'showCollectorFiles' });
            steps.push({ label: 'Generating MivuInstaller', type: 'generatingBinary' });

            this.allowConfigs = needsCollector || needsAlloy;
            return filterTemplate(steps);
          };

          if (this.configAssociation === "Agent"){
            this.multiformTemplate = buildTemplate([], { withAgentStep: true });
          }

          else if (this.configAssociation === "Group"){
            if (!this.agent?.group) return;

            const selectedGroupConfigs = await getApplicableConfigs({
              org: this.agent.organization,
              operating_system: this.selectedConfig,
              group: this.agent.group,
              spaceType: this.spaceType
            });

            this.multiformTemplate = buildTemplate(selectedGroupConfigs);
          }

          else if (this.configAssociation === "Organisation" && this.agent?.organization){
            // Clear any group from a previous Group flow so this is org-scoped only.
            this.agent.group = null;

            const selectedOrgConfigs = await getApplicableConfigs({
              org: this.agent.organization,
              operating_system: this.selectedConfig,
              spaceType: this.spaceType
            });

            this.multiformTemplate = buildTemplate(selectedOrgConfigs);
          }
        } catch (error) {
          console.error(error)
        }
      },

      incrementProgress() {
        this.progress += 1;
      },

      async startTutorial() {
        if (this.configSelection) {
          const originalOrgs = [...this.orgs];
          const originalSelectedOrg = this.selectedOrg;
          const originalSelectedConfig = this.selectedConfig;
          const originalSelectedSubset = this.selectedSubset;

          this.orgs = [{ id: -1, name: 'Tutorial Organisation' }];

          await this._start([
            { element: '#agent-config-org', action: 'select', value: -1, message: 'Select the organisation this agent belongs to.' },
            { element: '#agent-config-os', action: 'select', value: 'Windows', message: 'Select the operating system type for this agent.' },
            { element: '#agent-config-os-type',  action: 'select', value: 'serverspace', message: 'Select the operating system type for this agent.'},
            { element: '#agent-arch', action: 'select', value: 'x86_64', message: 'Select the architecture type.' },
          ], () => {
            this.orgs = originalOrgs;
            this.selectedOrg = originalSelectedOrg;
            this.selectedConfig = originalSelectedConfig;
            this.selectedSubset = originalSelectedSubset;
          });
        } else if (this.chooseConfigType) {
          const originalConfigAssociation = this.configAssociation;

          await this._start([
            { element: '#agent-config-type', action: 'select', value: 'Organisation', message: 'Choose how to associate the agent configuration — by Organisation, Group, or Agent.' },
          ], () => {
            this.configAssociation = originalConfigAssociation;
          });
        } else if (this.showAgent) {
          const originalLicenseState = this.agent.license_state;
          const originalHostname = this.agent.hostname;
          const originalUsername = this.agent.username;

          await this._start([
            { element: '#license-state-Active', action: 'click', message: 'Set the license state for this agent.' },
            { element: '#operating_system', action: 'display', message: 'The operating system is locked to your initial selection.' },
            { element: '#organization', action: 'display', message: 'The organisation is locked to your initial selection.' },
            { element: '#group', action: 'display', message: 'Assign this agent to an agent group.' },
            { element: '#hostname', action: 'type', value: 'agent-hostname', message: 'Enter the hostname for this agent.' },
            { element: '#username', action: 'type', value: 'admin', message: 'Enter the username for this agent.' },
            { element: '#submit-agent-btn', action: 'display', message: 'Click to save the agent.' },
          ], () => {
            this.agent.license_state = originalLicenseState;
            this.agent.hostname = originalHostname;
            this.agent.username = originalUsername;
          });
        }
      },

      checkPermissions(){
        this.canChangeAgent = useStore.hasPermission("Can change agents");
        this.canAddAgent = useStore.hasPermission("Can add agents");
      },

      async fetchWorkspaceUser(){
        const response = await getMyAccount();
        this.workspaceUser = response?.username;
      },

      async fetchWorkspaceUsers(){
        const orgData = this.orgs.find((item) => item.id == this.selectedOrg)
        const response = await getWorkspaceUsersbyOrg(orgData);
        this.workspaceUsers = response;
      },

      downloadFile() {
        if (this.product && this.product.link) {
          const link = this.$refs.downloadLink;
          link.href = this.product.link;
          link.download = '';
          link.click();
        }
      },

      async fetchGroups(){
        try {
          this.agent_groups = await getAgentGroups();
          this.filterGroupsByOrg();
        } catch (error) {
          console.error("Error fetching groups:", error);
        }
      },


      filterGroupsByOrg() {
        if (this.selectedOrg) {
          this.agent_groups = this.agent_groups.filter(
            (item) => item.organization === this.selectedOrg
          );
        } else {
          this.agent_groups = this.agent_groups;
        }
      },

      async fetchOrg(){
        try {
          this.orgs = await getOrgs();
        } catch (error) {
          console.error("Error fetching organization:", error);
        }
      },

      async fetchProducts(agentID){
        try {
          const products = await getProducts();
          this.product = products.find((item) => item.agent == agentID);

        } catch (error) {
          console.error("Error fetching products:", error);
        }
      },

      formatDateTime(dateString) {
        return new Date(dateString).toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZoneName: "short",
        });
      },

      async fetchAgent(agentId) {
        this.loading = true;
        this.error = null;
        try {
          const agents = await getAgents();
          this.agent = agents.find(item => item.licensekey == agentId)
          if (this.agent) this.originalAgentSnapshot = JSON.stringify(this.agent);
        } catch (error) {
          console.error("Error fetching agent:", error);
          this.error =
            "An error occurred while fetching agent details. Please try again later.";
        } finally {
          this.loading = false;
        }
      },
      
      async saveAgent() {
        this.loading = true;
        this.error = null;
        
        try {
          const agentData = {
            ...this.formattedAgent, 
            users: this.workspaceUsers
          };

          if (this.isNewAgent) {

            agentData.org_key = this.orgs
            .find(item => item.id == agentData.organization)?.org_key;
            const response = await createAgent(agentData);
            this.agent = response;

            // Inno-server expecting these fields
            this.multistepAgent = response.licensekey;
            this.multistepToken = response.token;

            // Increment and movve to next step
            this.handleNextStep()
          } 
          
          else {
            agentData.organization = this.agent.organization
            await updateAgent(this.agent.licensekey, agentData);
            this.$router.push({ name: "AgentView" })
          }

        } catch (error) {
          this.fieldErrors = {};
          const errData = error?.response?.data;
          if (errData) {
            for (const key in errData) {
              const value = errData[key];

              if (Array.isArray(value)) {
                this.fieldErrors[key] = value[0];
              } 
              else if (typeof value === 'object' && value !== null) {
                const nestedFirstKey = Object.keys(value)[0];
                const nestedMessages = value[nestedFirstKey];

                if (Array.isArray(nestedMessages)) {
                  this.fieldErrors[key] = nestedMessages[0];
                }
              }
            }
          }

          console.error("Error saving organization:", error);
        } finally {
          this.loading = false;
        }
      },


      triggerFileInput() {
        if (this.files.length < 4) {
          this.$refs.fileInput.click();
        }
      },
      
      handleFileUpload(event) {
        const selectedFiles = Array.from(event.target.files);
        
        const remainingSlots = 4 - this.files.length;

        const filesToAdd = selectedFiles.slice(0, remainingSlots);
        
        this.files = [...this.files, ...filesToAdd];
        event.target.value = '';
        
        if (selectedFiles.length > remainingSlots) {
          this.binaryError = `Only ${remainingSlots} more file(s) allowed. Maximum is 4 files.`;
        }
      },
      
      removeFile(index) {
        this.files.splice(index, 1);
      },

      async fetchNoAgentProducts(){
        try {
          const products = await getNoAgentProducts();
          this.orgInstallers = products.filter(item => item.group == null);
          this.groupInstallers =  products.filter(item => item.group !== null);

          this.groupInstallers.forEach(installer => {
            installer.organization_name = this.orgs.find(item => item.id === installer.organization)?.name;
            installer.group_name = this.agent_groups.find(item => item.id === installer.group)?.name
          });

        } catch (error) {
          console.error("Error fetching installer:", error);
        }
      },

      installerAlreadyExists(installers, checkGroup = false) {
        const targetArch          = this.selectedArch || "";
        const targetOS            = this.selectedConfig || "";
        const targetInstallerType = this.selectedSubset?.toLowerCase() || "";
        return installers.some(
          installer => installer?.organization === this.agent?.organization &&
            installer?.operating_system === targetOS &&
            (installer?.installer_type || "") === targetInstallerType &&
            installer?.architecture === targetArch &&
            (!checkGroup || installer?.group === this.agent?.group)
        );
      },

      binaryGenerationFailure(){
        this.generatingBinary = false;
        this.binaryGenerationFailed = true;
      },
      
      async submitForm() {
        if (this.selectedConfig === 'Linux') {
          await this.submitLinuxBuild();
        } else if (this.selectedConfig === 'Windows') {
          await this.submitWindowsBuild();
        }
      },

      async submitWindowsBuild() {
        await this.submitPullBuild(process.env.VUE_APP_INNO_SERVER_URL, 'MivuInstaller.exe');
      },

      async submitLinuxBuild() {
        await this.submitPullBuild(
          process.env.VUE_APP_LINUX_BUILDER_URL,
          `MivuInstaller_${this.selectedArch}_${this.selectedConfig}`
        );
      },

      async submitPullBuild(builderUrl, filename) {
        const validationError = this.validateInstallers();
        if (validationError) {
          this.handleBinaryError(validationError);
          return;
        }

        try {
          const manifest = await getBuildManifest({
            organization: this.agent.organization,
            os_subset: this.selectedSubset,
            architecture: this.selectedArch,
            group: this.agent.group,
            agent: this.multistepAgent,
          });
          if (!manifest || !Array.isArray(manifest.binaries)) {
            throw new Error('Missing Core configuration files. Please contact support!');
          }
        } catch (error) {
          console.error(error);
          this.handleBinaryError(this.manifestErrorMessage(error));
          return;
        }

        const formData = new FormData();
        this.appendLicenseData(formData);
        formData.append('organization', String(this.agent.organization ?? ''));
        formData.append('agentLicense', this.multistepAgent || '');
        this.appendProductMetadata(formData, filename);
        this.appendCollectorConfigs(formData);

        try {
          const jobId = await this.submitToBuildServer(formData, builderUrl, filename);
          if (!jobId) return;
          this.onBuildStarted();
        } catch (error) {
          this.handleSubmissionError(error);
        }
      },

      manifestErrorMessage(error) {
        const missing = error?.response?.status === 409 ? error.response.data?.missing : null;
        if (missing) {
          const parts = [];
          if (missing.binaries?.length) parts.push(`Missing required binaries: ${missing.binaries.join(', ')}`);
          if (missing.configs?.length) parts.push(`Missing required configs: ${missing.configs.join(', ')}`);
          if (parts.length) return parts.join('; ');
        }
        return error?.message || 'Build manifest request failed';
      },

      validateInstallers() {
        if (this.configAssociation === "Organisation" && this.installerAlreadyExists(this.orgInstallers)) {
          return "An Organisation level MivuInstaller already exists";
        }

        if (this.configAssociation === "Group" && this.installerAlreadyExists(this.groupInstallers, true)) {
          const groupKey = this.agent_groups.find(group => group.id === this.agent.group)?.name;
          return `A Group MivuInstaller already exists for ${groupKey}`;
        }

        return null;
      },

      skipProxy() {
        this.proxyUrl = "";
        this.clearFieldError('proxy_url');
        this.handleNextStep();
      },

      applyProxy() {
        const proxy = (this.proxyUrl || "").trim();
        if (proxy) {
          try {
            new URL(proxy);
          } catch {
            this.fieldErrors['proxy_url'] = 'Must be a valid URL (e.g. http://proxy.example.com:3128)';
            return;
          }
        }
        this.proxyUrl = proxy;
        this.handleNextStep();
      },

      appendLicenseData(formData) {
        const agentLicense = this.multistepAgent || "";
        const agentToken = this.multistepToken || "";
        const orgLicense = this.orgs.find(org => org.id === this.agent.organization)?.org_key || "";
        const groupKey = this.agent_groups.find(group => group.id === this.agent.group)?.name || "";
        formData.append('licensekey', agentLicense);
        formData.append('token', agentToken);
        formData.append('orgkey', orgLicense);
        formData.append('group', groupKey);
        formData.append('baseurl', process.env.VUE_APP_API_WORKSPACE_URL);
        formData.append('osSubset', this.selectedSubset || "");
        formData.append('architecture', this.selectedArch || "");
        formData.append('proxyURL', this.proxyUrl || "");
      },


      appendProductMetadata(formData, filename) {
        formData.append('configAssociation', this.configAssociation || '');
        formData.append('groupId', String(this.agent.group ?? ''));
        formData.append('operatingSystem', this.selectedConfig || '');
        formData.append('installerType', (this.selectedSubset || '').toLowerCase());
        formData.append('users', JSON.stringify(this.workspaceUsers || []));
        formData.append('productName', filename);
      },

      appendCollectorConfigs(formData) {
        for (const file of this.files) {
          formData.append('CollectorConfigs', file);
        }
      },

      async submitToBuildServer(formData, serverUrl, filename) {
        try {
          const res = await fetch(`${serverUrl}/build-installer-v2`, {
            method: "POST",
            body: formData
          });

          if (!res.ok) {
            const error = await this.safeJson(res);
            this.handleBinaryError(error?.error || "Build request failed");
            return null;
          }

          const { jobId } = await this.safeJson(res);

          // Hand the job off to the global store, which polls, downloads and
          // saves it in the background. Because only the jobId + this snapshot
          // are persisted, a reload or navigation can no longer lose the build.
          buildJobsStore.registerJob({
            jobId,
            serverUrl,
            os: this.selectedConfig,
            filename,
            label: this.buildJobLabel(filename),
            save: {
              configAssociation: this.configAssociation,
              organization: this.agent.organization,
              group: this.agent.group,
              agentLicense: this.multistepAgent,
              selectedConfig: this.selectedConfig,
              selectedSubset: this.selectedSubset,
              selectedArch: this.selectedArch,
              workspaceUsers: this.workspaceUsers,
              proxyUrl: this.proxyUrl,
            },
          });

          return jobId;
        } catch (error) {
          console.error('Failed to submit:', error);
          this.handleBinaryError(error.message);
          return null;
        }
      },

      buildJobLabel(filename) {
        const host = this.agent?.hostname;
        const parts = [host, this.selectedConfig, this.selectedArch].filter(Boolean);
        return parts.length ? parts.join(' · ') : filename;
      },

      onBuildStarted() {
        // The build now runs in the background store; surface a success state
        // pointing the user to the Downloads tab instead of blocking here.
        this.generatingBinary = false;
        this.buildStarted = true;
        this.multiformComplete = true;
        this.configAssociation = null;
      },

      async safeJson(response) {
        const ct = response.headers.get('content-type') || '';
        if (!ct.includes('application/json')) {
          throw new Error(`Builder service unavailable`);
        }
        return response.json();
      },

      handleBinaryError(errorMessage) {
        this.binaryError = errorMessage;
        this.binaryGenerationFailure();
      },

      handleSubmissionError(error) {
        this.configAssociation = null;
        this.binaryGenerationFailure();
        console.error('Submission failed:', error);
      },

      warnIfBuilding(event) {
        if (!this.generatingBinary) return;
        event.preventDefault();
        event.returnValue = '';
      }
  }
}
</script>

<style scoped>
.tracker-fade-in {
  animation: tracker-fade-in 0.3s ease-out;
}
@keyframes tracker-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style> 